import type { CSSProperties, FC } from 'react';
import { Col, DatePicker, Divider, Row, Segmented } from 'antd';
import { TbArrowNarrowRight, TbCalendarCode, TbPlus } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { ButtonSkeleton, MetricComparisonDropdown, MetricLineItemDropdown } from '@/shared';
import type { MetricChartCompany } from '@/shared/components/data-visualization/recharts';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import type { SetStateMethod } from '@/types';
import { dayjs } from '@/utilities';

type DayjsValue = ReturnType<typeof dayjs>;

interface MetricHeaderProps {
    company?: MetricChartCompany;
    comparisonCompanies?: MetricChartCompany[];
    endDate: DayjsValue;
    investment?: unknown;
    lineItemLabels?: Partial<Record<string, string>>;
    lineItems: string[];
    onComparisonCompaniesChange?: (companies: MetricChartCompany[]) => void;
    segmentedValue: string;
    setChartedLineItem: SetStateMethod<string>;
    setEndDate: SetStateMethod<DayjsValue>;
    setSegmentedValue: SetStateMethod<string>;
    setStartDate: SetStateMethod<DayjsValue>;
    setTags: SetStateMethod<string[]>;
    startDate: DayjsValue;
    tags: string[];
    view?: 'company' | 'economic' | 'investment';
}

const { RangePicker } = DatePicker;

const MetricHeader: FC<MetricHeaderProps> = ({
    company,
    comparisonCompanies = [],
    endDate,
    lineItemLabels,
    lineItems,
    onComparisonCompaniesChange,
    segmentedValue,
    setChartedLineItem,
    setEndDate,
    setSegmentedValue,
    setStartDate,
    setTags,
    startDate,
    tags,
    view = 'economic',
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const connectedRowStyle: CSSProperties | undefined =
        view === 'economic'
            ? undefined
            : {
                  borderBottom: `1px solid ${token.colorBorder}`,
                  padding: '1.5rem',
              };

    if (!tags.length) {
        return (
            <div style={connectedRowStyle}>
                <Row style={{ color: token.colorTextTertiary, marginTop: view === 'economic' ? '0.5rem' : 0 }}>
                    <Col flex='auto'>
                        <ButtonSkeleton size='small' style={{ width: 16 * 16 }} />
                    </Col>
                    <Col flex='none'>
                        <ButtonSkeleton size='small' style={{ width: 16 * 16 }} />
                    </Col>
                </Row>
            </div>
        );
    }

    return (
        <div style={connectedRowStyle}>
            <Row gutter={[16, 0]}>
                <Col span={12}>
                    <div style={{ alignItems: 'center', display: 'flex' }}>
                        <MetricLineItemDropdown
                            lineItemLabels={lineItemLabels}
                            lineItems={lineItems}
                            setChartedLineItem={setChartedLineItem}
                            setTags={setTags}
                            tags={tags}
                        >
                            <DropdownButton disabled={tags.length >= 5} size='small'>
                                <TbPlus style={{ verticalAlign: '-0.125rem' }} />
                                Add to Chart
                            </DropdownButton>
                        </MetricLineItemDropdown>
                        {company && onComparisonCompaniesChange ? (
                            <>
                                <Divider style={{ marginInline: '0.75rem' }} type='vertical' />
                                <MetricComparisonDropdown
                                    company={company}
                                    value={comparisonCompanies}
                                    onChange={onComparisonCompaniesChange}
                                />
                            </>
                        ) : null}
                    </div>
                </Col>
                <Col span={4} />
                <Col span={8}>
                    <div style={{ alignItems: 'center', display: 'flex', width: '100%' }}>
                        <RangePicker
                            allowClear={false}
                            disabledDate={(current) => {
                                const now = dayjs();
                                const fifteenYearsAgo = now.subtract(15, 'year');

                                return current.isBefore(fifteenYearsAgo) || current.isAfter(now);
                            }}
                            picker='year'
                            placement='bottomRight'
                            separator={
                                <TbArrowNarrowRight
                                    size='1rem'
                                    style={{ color: token.colorTextTertiary, verticalAlign: '-0.2rem' }}
                                />
                            }
                            size='small'
                            style={{ flex: 1, minWidth: 0 }}
                            suffixIcon={<TbCalendarCode size='1rem' style={{ verticalAlign: '-0.2rem' }} />}
                            value={[startDate, endDate]}
                            onCalendarChange={(values) => {
                                const [nextStartDate, nextEndDate] = values ?? [];

                                if (nextStartDate) {
                                    setStartDate(dayjs(nextStartDate.valueOf()));
                                }

                                if (nextEndDate) {
                                    setEndDate(dayjs(nextEndDate.valueOf()));
                                }
                            }}
                        />
                        <Divider style={{ marginInline: '0.75rem' }} type='vertical' />
                        <Segmented
                            block
                            options={[
                                { label: 'Year', value: 'annual' },
                                { label: 'Quarter', value: 'quarter' },
                            ]}
                            size='small'
                            style={{ flex: 1, minWidth: 0 }}
                            value={segmentedValue}
                            onChange={setSegmentedValue}
                        />
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default MetricHeader;
